import { useMemo } from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import get from 'lodash/get'
import { useTeam } from '../../../contexts/TeamContext'
import { useDockerNetworks } from '../../../contexts/DockerNetworksContext'
import { useNodes } from '../../../contexts/NodesContext'
import { useProjects } from '../../../contexts/ProjectsContext'
import useNodeSelection from '../../../shared/hooks/useNodeSelection'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import NodeChipFilter from '../../../shared/components/NodeChipFilter'
import Page from '../../../shared/components/layouts/Page'
import PageHeader from '../../../shared/components/layouts/PageHeader'

const getDriverColor = (driver) => {
  switch (driver) {
    case 'bridge':
      return 'default'
    case 'overlay':
      return 'primary'
    case 'host':
      return 'secondary'
    case 'macvlan':
      return 'info'
    case 'none':
      return 'warning'
    default:
      return 'default'
  }
}

function NetworkIndexPage() {
  const { team } = useTeam()
  const { networks, loading: networksLoading, error } = useDockerNetworks()
  const { projectsById, loading: projectsLoading } = useProjects()
  const { nodes, loading: nodesLoading } = useNodes()

  const { activeNodeId, handleSelectNode } = useNodeSelection('networks.selectedNodeId', nodes)

  const loading = networksLoading || projectsLoading || nodesLoading

  const filteredNetworks = useMemo(() => {
    if (!activeNodeId) return networks
    return networks.filter((network) => {
      const project = get(projectsById, network.projectId)
      return project?.nodeIds?.includes(activeNodeId)
    })
  }, [networks, projectsById, activeNodeId])

  const pendingCountByNodeId = useMemo(() => {
    const counts = {}
    networks.forEach((network) => {
      if (network.pendingChanges) {
        const project = get(projectsById, network.projectId)
        project?.nodeIds?.forEach((nodeId) => {
          counts[nodeId] = (counts[nodeId] || 0) + 1
        })
      }
    })
    return counts
  }, [networks, projectsById])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="networks.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="networks.title" />}
        entities={{ team }}
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, overflow: 'hidden', minHeight: 0, width: '100%' }}>
        <Box sx={{ flexShrink: 0, minWidth: { md: 200 }, borderRight: { xs: 0, md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider', overflowY: { xs: 'hidden', md: 'auto' }, py: { xs: 1, md: 3 }, px: 2 }}>
          <NodeChipFilter
            nodes={nodes}
            activeNodeId={activeNodeId}
            onSelect={handleSelectNode}
            pendingCountByNodeId={pendingCountByNodeId}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', py: 3, px: 2 }}>
          {filteredNetworks.length === 0 ? (
            <Typography color="text.secondary">
              <FormattedMessage id="networks.empty" />
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id="networks.index.columns.name" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="networks.index.columns.dockerName" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="networks.index.columns.driver" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="networks.index.columns.description" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="networks.index.columns.createdAt" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredNetworks.map((network) => (
                    <TableRow key={network.id}>
                      <TableCell>{network.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {network.dockerName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={network.driver}
                          color={getDriverColor(network.driver)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{network.description || '-'}</TableCell>
                      <TableCell>
                        {network.createdAt ? (
                          <FormattedDate
                            value={network.createdAt}
                            year="numeric"
                            month="short"
                            day="numeric"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Page>
  )
}

export default NetworkIndexPage
